/**
 * Compact date picker with calendar dropdown.
 * Calendar position configurable for different contexts (dropdowns vs panels).
 */

import { CalendarIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { format } from 'date-fns';
import { useRef, useState } from 'react';

import { Calendar } from '@/components/ui/calendar';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';
import { cn } from '@/lib/utils';
import { parseISODate } from '@/utils/dateUtils';

interface DatePickerProps {
  /** Date value as ISO string (YYYY-MM-DD) or null */
  date: string | null | undefined;
  /** Callback when date changes */
  onChange: (date: Date | null) => void;
  /** Placeholder text when no date selected */
  placeholder?: string;
  /** Additional className for the container */
  className?: string;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /**
   * Calendar position relative to the input.
   * - 'above': Opens above input (default, best for dropdowns to avoid scroll issues)
   * - 'below': Opens below input (best for panels/sheets where above may be clipped)
   */
  calendarPosition?: 'above' | 'below';
  /**
   * Horizontal anchor for the calendar popup.
   * - 'left': Aligns to left edge of input (default)
   * - 'right': Aligns to right edge of input (use for rightmost fields to avoid overflow)
   */
  calendarAnchor?: 'left' | 'right';
}

export const DatePicker = ({
  date,
  onChange,
  placeholder = 'Select date',
  className,
  disabled = false,
  calendarPosition = 'above',
  calendarAnchor = 'left',
}: DatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close calendar when clicking outside
  useOnClickOutside(containerRef, () => setIsOpen(false));

  const handleDateSelect = (newDate: Date | undefined) => {
    onChange(newDate || null);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const handleCalendarKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const selectedDate = date ? parseISODate(date) : undefined;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Date trigger - matches DataTable search input styling */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-full flex items-center justify-between gap-2',
          // Height matches search input
          'h-8 px-3 text-xs',
          // Border matches search input exactly
          'rounded-lg border border-zinc-950/10 dark:border-white/10',
          'bg-white dark:bg-zinc-900',
          'hover:border-zinc-950/20 dark:hover:border-white/20',
          'focus:outline-none focus:border-zinc-950/20 dark:focus:border-white/20',
          'transition-colors',
          // Text colors
          date
            ? 'text-zinc-950 dark:text-white'
            : 'text-zinc-400 dark:text-zinc-500',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        <span className="flex items-center gap-2 truncate">
          <CalendarIcon className="size-3.5 text-gray-400 dark:text-zinc-500 shrink-0" />
          {selectedDate ? format(selectedDate, 'MMM d, yyyy') : placeholder}
        </span>
        {date && !disabled && (
          <XMarkIcon
            className="size-3.5 text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300 shrink-0"
            onClick={handleClear}
          />
        )}
      </button>

      {/* Calendar - position configurable for different contexts */}
      {isOpen && !disabled && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={cn(
            'absolute z-50',
            // Vertical position
            calendarPosition === 'below' ? 'top-full mt-1' : 'bottom-full mb-1',
            // Horizontal anchor - right anchor prevents overflow in constrained panels
            calendarAnchor === 'right' ? 'right-0' : 'left-0',
            'bg-white dark:bg-zinc-900 rounded-lg shadow-lg',
            'border border-zinc-200 dark:border-zinc-700',
          )}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleCalendarKeyDown}
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            className="p-2"
          />
        </div>
      )}
    </div>
  );
};
